import {
  afterNextRender,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  QueryList,
  ViewChild,
  ViewChildren,
  ViewEncapsulation
} from '@angular/core';
import {
  CommonModule,
  DOCUMENT,
  isPlatformBrowser,
  NgOptimizedImage,
  NgStyle
} from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';
import {
  additionalAdviceListMock,
  adviceListLandingMock,
  adviceListMock,
  bgVideoUrl,
  demoListMock,
  performanceIndicatorsMock
} from '@App/modules/affiliate-flow/mock/affiliate-invite.mock';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ScreenSizeService } from '@Shared/services';
import { RestApiService, UserCommonApiService } from '@App/common/services';
import { EXTERNAL_URLS } from '@Shared/config';
import { BusinessPitchWrapped } from '@App/common/models';
import { LandingPageService } from '@App/pages/landing-page/landing-page.service';
import { PitchCardComponent } from '@App/modules/pitch-card-shared/components/pitch-card/pitch-card.component';
import { LandingTypeEnum } from '@App/common/enums';
import { map } from 'rxjs/operators';
import { AuthService } from '@App/pages/auth/services';
import { UniqueComponentId } from 'primeng/utils';
import { PitchCardModalsWrapperService } from '@App/modules/pitch-card-shared/services';
import { PlayerMuxComponent } from '@Shared/components/player-mux/player-mux.component';
import { ReviewListComponent } from '@App/modules/affiliate-flow/components/review-list/review-list.component';
import { FaqComponent } from '@App/modules/affiliate-flow/components/faq/faq.component';
import { NotificationService } from '@Shared/services';
import { DialogModule } from 'primeng/dialog';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-landing-page',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    PlayerMuxComponent,
    ReviewListComponent,
    FaqComponent,
    NgStyle,
    NgOptimizedImage,
    DialogModule
  ],
  templateUrl: './landing-page.component.html',
  styleUrls: ['./landing-page.component.scss'],
  encapsulation: ViewEncapsulation.Emulated
})
export class LandingPageComponent implements OnInit, AfterViewInit, OnDestroy {
  sliderList: any[];
  readonly heroVideoPlaceholders: readonly string[] = [
    'assets/image-video-placeholders/video-placeholder-1.png',
    'assets/image-video-placeholders/video-placeholder-2.jpg',
    'assets/image-video-placeholders/video-placeholder-3.jpg',
    'assets/image-video-placeholders/video-placeholder-4.png',
    'assets/image-video-placeholders/video-placeholder-5.png',
    'assets/image-video-placeholders/video-placeholder-6.jpg',
    'assets/image-video-placeholders/video-placeholder-7.jpg',
    'assets/image-video-placeholders/video-placeholder-8.png',
    'assets/image-video-placeholders/video-placeholder-9.jpg',
    'assets/image-video-placeholders/video-placeholder-10.jpg',
    'assets/image-video-placeholders/video-placeholder-11.jpg',
    'assets/image-video-placeholders/video-placeholder-12.jpg'
  ];
  readonly leftVideoColumn = this.heroVideoPlaceholders.slice(0, 6);
  readonly rightVideoColumn = this.heroVideoPlaceholders.slice(6, 12);
  readonly demoVideoUrl =
    'https://pitch59-dev.s3.us-east-1.amazonaws.com/landing_videos/PITCH59_VIDEO.mp4';
  isDemoDialogVisible = false;
  private heroVideosReady = new Set<string>();
  demoList: string[] = demoListMock;
  performanceIndicators: any[] = performanceIndicatorsMock;
  adviceList: any[] = adviceListMock;
  additionalAdviceList: any[] = additionalAdviceListMock;
  discountCode: string;
  @ViewChild('businessCard', { static: false })
  activeVideo = 0;
  muxDemoVideoId: string;
  @ViewChild('PreviewPitchBlock', { static: false })
  previewPitchBlock: ElementRef;
  @ViewChild('SliderBlock', { static: false }) sliderBlock: ElementRef;
  @ViewChild('SliderBlockVideo', { static: false })
  sliderBlockVideo: any;
  @ViewChildren(PitchCardComponent) slides: QueryList<PitchCardComponent>;
  @ViewChildren('heroVideo', { read: ElementRef })
  heroVideos!: QueryList<ElementRef<HTMLVideoElement>>;
  @ViewChild('DemoBlock', { static: false }) demoBlock: ElementRef;
  @ViewChild('DemoBlockVideo', { static: false })
  demoBlockVideo: any;
  @ViewChild('FaqBlock', { static: false }) faqBlock: ElementRef;
  @ViewChild('FAQComponent', { static: false }) faqComponent: any;
  @ViewChild('HowtoSection', { static: false }) howtoSection: ElementRef;
  @ViewChild('scrollContent', { static: false })
  scrollContent!: ElementRef<HTMLDivElement>;
  bgVideoUrl = bgVideoUrl;
  isVideoActive: boolean;
  isMobile: boolean;
  isTablet: boolean;
  previewCardsList: any[];
  responsiveOptions: any[];
  scaleFactor: number = 1;
  isListLoaded: boolean;
  landingType: LandingTypeEnum;
  isPortraitMode: boolean = false;
  isDesktop: boolean;
  heroLayoutReady = false;
  partnersListUrls: any[];
  @Input() uniqueId: string = UniqueComponentId();
  testimonials = [
    {
      text: 'Pitch59 helped me get more referrals in my first month than any other tool!',
      author: 'Janet R.'
    },
    {
      text: 'The PitchCard is a game changer for networking and building trust.',
      author: 'Jeff B.'
    },
    {
      text: 'I love how easy it is to share my story and get real results.',
      author: 'David L.'
    }
  ];
  howtoVideos: any[] = [
    {
      title: 'How do I use a PitchCard?',
      image: 'assets/images/landing-page/image-pitchcard.jpg',
      playIcon: 'assets/images/landing-page/icon-video-play.svg',
      videoUrl: 'Jh701eVs1W1gkccLuH6DLBqRD003YEsUTH',
      isPlaying: false
    },
    {
      title: 'How do I create a PitchCard?',
      image: 'assets/images/landing-page/image-create.jpg',
      playIcon: 'assets/images/landing-page/icon-video-play.svg',
      videoUrl: 'j02gx2017wnhB01nzfug65UCpkKDImg6Vmn',
      isPlaying: false
    },
    {
      title: 'Networking with a PitchCard',
      image: 'assets/images/landing-page/image-networking.jpg',
      playIcon: 'assets/images/landing-page/icon-video-play.svg',
      videoUrl: 'pkzuYi9O6ORxwdeWiqETsOPUFq5qpdWj',
      isPlaying: false
    },
    {
      title: 'The Pitch59 story',
      image: 'assets/images/landing-page/image-story.jpg',
      playIcon: 'assets/images/landing-page/icon-video-play.svg',
      videoUrl: 'mK8b2Nh02x00dO16VO5rN4hX3L3mWjZhsA',
      isPlaying: false
    }
  ];
  readonly whyPitchcardItems: ReadonlyArray<{
    image: string;
    alt: string;
    title: string;
    desc: string;
  }> = [
    {
      image: 'assets/images/landing-page/icon-share-story.png',
      alt: 'Share Your Story',
      title: 'Share Your Story',
      desc: "Your PitchCard contains your 59-second elevator pitch. Don't want the video? No problem, just your written only pitch."
    },
    {
      image: 'assets/images/landing-page/icon-get-referrals.png',
      alt: 'Get Referrals',
      title: 'Get Referrals',
      desc: 'Anyone, with or without the app, can refer and make custom message 3-way introductions for you in less than 10 seconds.'
    },
    {
      image: 'assets/images/landing-page/icon-network.png',
      alt: 'Network With others',
      title: 'Network With others',
      desc: 'When you have a PitchCard, you can search and automatically get networked with other PitchCard owners to build your referral partnerships.'
    },
    {
      image: 'assets/images/landing-page/icon-track-performance.png',
      alt: 'See Your Analytics',
      title: 'See Your Analytics',
      desc: "View helpful metrics like number of times your pitchcard has been shared, who's sharing it, how they are sharing it, and more!"
    },
    {
      image: 'assets/images/landing-page/icon-boost-visibility.png',
      alt: 'Access Success Tips',
      title: 'Access Success Tips',
      desc: 'View and complete the Success Tips to build a strong network and get more referrals.'
    },
    {
      image: 'assets/images/landing-page/icon-create-pitch.png',
      alt: 'Create Your Pitch',
      title: 'Create Your Pitch',
      desc: 'Create your elevator pitch by answering 5 simple questions; our AI-enhanced Elevator Pitch Creator will do the rest!'
    }
  ];
  blogList = [
    {
      image: 'assets/images/landing-page/blog/referal-sprints.png',
      mobileImage: 'assets/images/landing-page/blog/referal-sprints-mobile.png',
      title: 'Referral Sprints: A More Intentional Way to Generate Referrals',
      description:
        'Learn how Referral Sprints help professionals create intentional introductions, activate their network, and generate more referrals.',
      url: EXTERNAL_URLS.REFERAL_SPRINTS
    },
    {
      image: 'assets/images/landing-page/blog/what-can-happen.png',
      title:
        'What Can Happen in Just 15 Minutes? The Referral Sprint Experiment',
      description:
        'Three Referral Sprints. 38 intros. Approximately 28 meetings. See why intentional referrals create momentum. You already have the network to make it work.',
      url: EXTERNAL_URLS.WHAT_CAN_HAPPEN
    },
    {
      image: 'assets/images/landing-page/blog/places.png',
      title: '15 Places to Share Your Digital Business Card',
      description:
        'Discover 15 smart places to share your digital business card. Learn how to get more visibility, build trust, and generate more referrals.',
      url: EXTERNAL_URLS.PLACES_TO_SHARE
    }
  ];
  sliderSectionTitle: string;
  thumbStyle: any = {};
  @ViewChildren('benefitCard') benefitCards!: QueryList<ElementRef>;
  private notificationService = inject(NotificationService);
  private threshold: number = 50;
  private endX: number;
  private endY: number;
  private startX: number;
  private startY: number;
  private currentIdx = 1;
  private centreSlide: any;
  private isAuthorized: boolean;
  private slideTimer: any;
  private title: string;
  private subtitle: string;
  private dragging = false;
  private startScrollTop = 0;
  private mutationObserver: MutationObserver | undefined;
  private heroVideoObservers: IntersectionObserver[] = [];
  private heroVideoChangesSubscription?: Subscription;
  private heroVideoInitFrame?: number;
  private heroVideoProximityTimer?: ReturnType<typeof setInterval>;
  private scrollThrottleTimer: any;
  private restApiService = inject(RestApiService);

  constructor(
    public route: ActivatedRoute,
    private screenSizeService: ScreenSizeService,
    private router: Router,
    private userCommonService: UserCommonApiService,
    private cdr: ChangeDetectorRef,
    private landingPageService: LandingPageService,
    private authService: AuthService,
    private pitchCardModalsWrapperService: PitchCardModalsWrapperService,
    private metaService: Meta,
    private titleService: Title,
    @Inject(DOCUMENT) private doc: Document,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
    afterNextRender(() => {
      this.isMobile = this.screenSizeService.isMobile;
      this.isDesktop = this.screenSizeService.isDesktop;
      this.isTablet = this.screenSizeService.isTablet;
      this.heroLayoutReady = true;
      this.cdr.detectChanges();
      if (isPlatformBrowser(this.platformId)) {
        setTimeout(() => this.scheduleHeroVideoPreloading());
      }
    });

    if (this.route.snapshot?.queryParams?.discountCode) {
      this.discountCode = this.route.snapshot.queryParams.discountCode;
    }
    if (
      this.route.snapshot?.queryParams?.token &&
      this.router.url.includes('tenant-invite')
    ) {
      this.userCommonService
        .acceptInvite(this.route.snapshot?.queryParams?.token)
        .subscribe({
          next: () => {
            setTimeout(() =>
              this.notificationService.showNotification(
                'SUCCESS',
                'Your invite has been accepted'
              )
            );
          },
          error: () => {
            sessionStorage.setItem(
              'inviteToken',
              this.route.snapshot?.queryParams?.token
            );
          }
        });
    }
    switch (this.router.url.split('?')[0]) {
      case '/':
      case '/tenant-invite':
        this.sliderSectionTitle =
          'Growing Your Business is Easier With a PitchCard';
        this.title = 'Stand Out. Be Remembered. Get More Referrals.';
        this.subtitle =
          'PitchCards are the world’s most powerful business card for introductions, referrals, and networking.';
        this.sliderList = [
          {
            title: 'Share Your 59-second elevator pitch with everyone',
            subtitle:
              'Clearly communicate your value proposition and help people get to know, like, and trust you.'
          },
          {
            title: 'Get Referrals in Just 5 Seconds',
            subtitle:
              'Make it easy for your customers, friends, and referral partners to introduce you to their contacts in just 5 seconds.'
          },
          {
            title: 'Grow Your Professional Circle',
            subtitle:
              'Network and become referral partners with thousands of other pros in the Pitch59 network.'
          }
        ];
        this.landingType = 1;
        this.muxDemoVideoId = 'VPIGB6XhcE3UPa5nqiqvDMYGCxbbaDOI';
        this.adviceList = adviceListLandingMock;
        break;
      case '/affiliate':
        this.sliderSectionTitle =
          'Growing Your Business is Easier With a PitchCard';
        this.title = 'Stand Out. Be Remembered. Get More Referrals.';
        this.subtitle =
          'PitchCards are the world’s most powerful business card for introductions, referrals, and networking.';
        this.sliderList = [
          {
            title: 'Share your 59-second elevator pitch with everyone',
            subtitle:
              'Clearly communicate your value proposition and help people get to know, like, and trust you.'
          },
          {
            title: 'Get Referrals in Just 5 Seconds',
            subtitle:
              'Make it easy for your customers, friends, and referral partners to introduce you to their contacts in just 5 seconds.'
          },
          {
            title: 'Grow Your Professional Circle',
            subtitle:
              'Network and become referral partners with thousands of other pros in the Pitch59 network.'
          }
        ];
        this.landingType = 0;
        this.muxDemoVideoId = 'VPIGB6XhcE3UPa5nqiqvDMYGCxbbaDOI';
        this.adviceList = adviceListMock;
        break;
      case '/jobs':
        this.landingType = 2;
        this.sliderSectionTitle =
          'Growing Job Opportunities is Easier With a PitchCard';
        this.title = 'Get Noticed. Get Hired.';
        this.subtitle =
          'PitchCards are the world’s most powerful introduction, referral, and networking tool.';
        this.sliderList = [
          {
            title: 'Share your Personalized Resume Card with ease',
            subtitle:
              'Clearly communicate your unique skillset and personality, helping people get to know, like, and trust you.'
          },
          {
            title: 'Get Referrals in Just 5 Seconds',
            subtitle:
              'Make it easy for your friends, family, and references to introduce you to their contacts in just 5 seconds.'
          },
          {
            title: 'Stand out in the applicant pool',
            subtitle:
              'Give your resume a human feel, and stand out compared to other applicants by letting your personality shine!'
          }
        ];
        this.muxDemoVideoId = 'VPIGB6XhcE3UPa5nqiqvDMYGCxbbaDOI';
        this.adviceList = adviceListLandingMock;
        break;
    }

    this.applyPageSeoTags();
  }

  get getTitleData() {
    return { title: this.title, subtitle: this.subtitle };
  }

  ngOnInit(): void {
    this.isAuthorized = this.userCommonService.isAuthenticated();
    this.isMobile = this.screenSizeService.isMobile;
    this.isDesktop = this.screenSizeService.isDesktop;
    this.isTablet = this.screenSizeService.isTablet;
    this.partnersListUrls = this.landingPageService
      .getPartnersImageList(this.isMobile)
      .filter((partner) => partner.title !== 'RNAOR');

    // Initialize isPortraitMode safely for SSR
    if (isPlatformBrowser(this.platformId)) {
      this.isPortraitMode = window.innerHeight > window.innerWidth;
    }

    setTimeout(() => {
      this.activeVideo = 0;
      if (this.sliderList[this.activeVideo]?.videoUrl) {
        this.isVideoActive = true;
      }
    });
    this.getPreviewCardsList();
    this.startTimer();

    // Add visibility change listener to pause/resume timer
    if (isPlatformBrowser(this.platformId)) {
      document.addEventListener(
        'visibilitychange',
        this.handleVisibilityChange
      );
    }

    this.responsiveOptions = [
      {
        breakpoint: '992px',
        numVisible: this.isMobile ? 1.5 : this.isDesktop ? 3 : 3.5,
        numScroll: this.isMobile ? 1.5 : 3
      },
      {
        breakpoint: '670px',
        numVisible: 1.5,
        numScroll: 2
      }
    ];
  }

  onContainerSizeChanged(event) {
    const { containerSize } = event;
    setTimeout(() => {
      this.scaleFactor = containerSize / 370;
    });
  }

  ngOnDestroy() {
    if (isPlatformBrowser(this.platformId)) {
      if (sessionStorage.getItem('DiscountCode')) {
        sessionStorage.removeItem('DiscountCode');
      }
      window.removeEventListener('resize', this.updateThumbBound);
      document.removeEventListener(
        'visibilitychange',
        this.handleVisibilityChange
      );
    }
    if (this.slideTimer) {
      clearInterval(this.slideTimer);
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
    this.disconnectHeroVideoObservers();
    this.heroVideoChangesSubscription?.unsubscribe();
    if (this.heroVideoInitFrame !== undefined) {
      cancelAnimationFrame(this.heroVideoInitFrame);
    }
    if (this.scrollThrottleTimer) {
      clearTimeout(this.scrollThrottleTimer);
    }
  }

  ngAfterViewInit() {
    this.cdr.detectChanges();
    this.initSliderSubscriptions();
    this.checkQueryParams();
    this.updateThumb();

    if (isPlatformBrowser(this.platformId)) {
      window.addEventListener('resize', this.updateThumbBound);
      this.heroVideoChangesSubscription = this.heroVideos.changes.subscribe(
        () => this.scheduleHeroVideoPreloading()
      );
      this.scheduleHeroVideoPreloading();
    }

    // Optimize mutation observer - only watch for structural changes
    if (
      this.scrollContent?.nativeElement &&
      isPlatformBrowser(this.platformId)
    ) {
      this.mutationObserver = new MutationObserver(() => {
        // Only update thumb if content structure changed, not text
        this.updateThumb();
      });
      this.mutationObserver.observe(this.scrollContent.nativeElement, {
        childList: true,
        subtree: false, // Don't watch subtree to reduce overhead
        characterData: false // Don't watch text changes
      });
    }
  }

  moveToSlide(number: number) {
    this.activeVideo = number;
    this.resetTimer();
    setTimeout(() => {
      const cards = this.benefitCards?.toArray();
      if (cards && cards[this.activeVideo]) {
        cards[this.activeVideo].nativeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest'
        });
      }
    });
  }

  moveToNextSlide() {}

  scrollTo(block: string) {
    const scrollOptions = {
      behavior: 'smooth',
      block: 'center'
    };
    switch (block) {
      case 'PreviewPitchBlock':
        this.previewPitchBlock.nativeElement.scrollIntoView(scrollOptions);
        this.centreSlide.startUnMuted(
          this.centreSlide?.initVideo &&
            this.centreSlide?.businessDetails?.isVideoPlaying
        );
        break;
      case 'SliderBlock':
        this.activeVideo = 0;
        this.sliderBlock.nativeElement.scrollIntoView(scrollOptions);
        this.sliderBlockVideo.player.currentTime(0);
        this.sliderBlockVideo.player.play();
        this.sliderBlockVideo.player.muted(false);
        this.sliderBlockVideo.muted = false;
        break;
      case 'DemoBlock':
        this.toggleMute(true, this.activeVideo);
        this.demoBlock.nativeElement.scrollIntoView(scrollOptions);
        if (this.demoBlockVideo.vPlayer.nativeElement.muted) {
          this.demoBlockVideo.vPlayer.nativeElement.click();
        }
        break;
      case 'FaqBlock':
        this.faqBlock.nativeElement.scrollIntoView(scrollOptions);
        this.faqComponent.faqItems.map((item) => (item.open = false));
        this.faqComponent.faqItems[4].open = true;
        this.faqComponent.faqItems[5].open = true;
        break;
    }
  }

  toggleMute(isMuted: boolean, activeVideo: number) {
    if (this.sliderList[activeVideo]?.isHaveSound) {
      this.sliderList[activeVideo].isMuted = isMuted;
      if (this.sliderList[activeVideo].isMuted) {
        this.sliderBlockVideo.player.muted(true);
        this.sliderBlockVideo.muted = true;
      } else {
        this.sliderBlockVideo.player.muted(false);
        this.sliderBlockVideo.muted = false;
      }
    } else {
      this.sliderBlockVideo.player.muted(true);
      this.sliderBlockVideo.muted = true;
    }
  }

  moveToCreate() {
    switch (this.landingType) {
      case LandingTypeEnum.AFFILIATE:
        this.router.navigate(['pricing'], {
          queryParams: { discountCode: this.discountCode }
        });
        break;
      case LandingTypeEnum.WELCOME:
        this.router.navigate(['pricing']);
        break;
      case LandingTypeEnum.RESUME:
        this.router.navigate(['pricing'], {
          queryParams: { type: 'resume' }
        });
        break;
    }
  }

  openDemoDialog(): void {
    this.isDemoDialogVisible = true;
  }

  onDemoDialogHide(): void {
    this.isDemoDialogVisible = false;
  }

  scrollToHowtoSection() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const howtoSection = document.getElementById('howto-section');
    if (howtoSection) {
      const elementRect = howtoSection.getBoundingClientRect();
      const absoluteElementTop = elementRect.top + window.pageYOffset;
      const middle =
        absoluteElementTop - window.innerHeight / 2 + elementRect.height / 2;

      window.scrollTo({
        top: middle,
        behavior: 'smooth'
      });
    }
  }

  setDiscountCode(discount: any) {
    sessionStorage.setItem('DiscountCode', discount);
    this.discountCode = discount;
  }

  onIndexChange(event: any) {
    const { currentIndex, currentOffset } = event;
    this.currentIdx = currentIndex + 1;
    if (this.previewCardsList.length > 1) {
      this.stopVideoPlaying();
    }
    setTimeout(() => {
      this.playInlineVideo(currentOffset);
      this.cdr.detectChanges();
    }, 0);
  }

  playInlineVideo(idx = 0) {
    this.centreSlide = this.slides.find((slide, index) => {
      if (idx === index) {
        slide.index = index;
      }
      return idx === index;
    });
    if (this.centreSlide) {
      this.centreSlide.initVideo = true;
      if (this.centreSlide?.businessDetails?.videoFileUrl) {
        this.centreSlide?.runVideo();
      } else {
        this.centreSlide.initVideo = false;
      }
    }
  }

  getDemoDetailsTopMargin() {
    if (this.landingType === 0) {
      if (this.isMobile) {
        return 'mt-4';
      } else {
        return 'mt-8';
      }
    } else {
      if (this.isMobile) {
        return 'mt-4';
      } else {
        return 'mt-0';
      }
    }
  }

  handlePitchCardTitleClick(event) {
    const { title, businessDetails } = event;
    this.pitchCardModalsWrapperService.setUniqueId(this.uniqueId);
    this.pitchCardModalsWrapperService.setCurrentBusiness(businessDetails);
    this.pitchCardModalsWrapperService.onTitleClick(title);
  }

  playHowtoVideo(index: number) {
    this.howtoVideos.forEach((video, i) => {
      if (i !== index) {
        video.isPlaying = false;
      }
    });

    this.howtoVideos[index].isPlaying = true;

    if (this.slideTimer) {
      clearInterval(this.slideTimer);
    }
  }

  onHowtoVideoEnded(index: number) {
    this.howtoVideos[index].isPlaying = false;

    // Resume timer after video ends
    this.startTimer();
  }

  enableVideoSound(index: number) {
    // This method will be called when user clicks on video
    // The click event should allow audio to play due to user interaction
  }

  onHeroVideoCanPlay(
    event: Event,
    column: 'left' | 'right' | 'row',
    copy: number,
    index: number
  ): void {
    const video = event.currentTarget as HTMLVideoElement | null;
    if (!video || video.dataset['preloadState'] !== 'auto') {
      return;
    }

    // Set both properties before play() so browsers consistently treat this
    // as muted autoplay, including after SSR hydration.
    video.defaultMuted = true;
    video.muted = true;

    void video
      .play()
      .then(() => this.onHeroVideoReady(column, copy, index))
      .catch(() => {
        // Keep the placeholder visible if autoplay or the media format is
        // rejected. The native `playing` event will reveal successful videos.
      });
  }

  onHeroVideoReady(
    column: 'left' | 'right' | 'row',
    copy: number,
    index: number
  ): void {
    this.heroVideosReady.add(`${column}-${copy}-${index}`);
  }

  isHeroVideoReady(
    column: 'left' | 'right' | 'row',
    copy: number,
    index: number
  ): boolean {
    return this.heroVideosReady.has(`${column}-${copy}-${index}`);
  }

  private scheduleHeroVideoPreloading(): void {
    if (this.heroVideoInitFrame !== undefined) {
      cancelAnimationFrame(this.heroVideoInitFrame);
    }

    this.heroVideoInitFrame = requestAnimationFrame(() => {
      this.heroVideoInitFrame = requestAnimationFrame(() => {
        this.heroVideoInitFrame = undefined;
        this.initializeHeroVideoPreloading();
      });
    });
  }

  private initializeHeroVideoPreloading(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.disconnectHeroVideoObservers();

    const videos = this.heroVideos
      .toArray()
      .map(({ nativeElement }) => nativeElement);

    if (!videos.length) {
      return;
    }

    if (!('IntersectionObserver' in window)) {
      videos.forEach((video) => this.promoteHeroVideo(video));
      return;
    }

    const videosByViewport = new Map<HTMLElement, HTMLVideoElement[]>();

    videos.forEach((video) => {
      const viewport = video.closest<HTMLElement>(
        '.video-column, .video-marquee'
      );
      if (!viewport) {
        this.promoteHeroVideo(video);
        return;
      }

      const viewportVideos = videosByViewport.get(viewport) ?? [];
      viewportVideos.push(video);
      videosByViewport.set(viewport, viewportVideos);
    });

    videosByViewport.forEach((viewportVideos, viewport) => {
      const isHorizontal = viewport.classList.contains('video-marquee');
      const viewportSize = isHorizontal
        ? viewport.clientWidth
        : viewport.clientHeight;
      const warmDistance = Math.round(viewportSize);
      const nearDistance = Math.round(viewportSize * 0.35);
      const warmMargin = isHorizontal
        ? `0px ${warmDistance}px`
        : `${warmDistance}px 0px`;
      const nearMargin = isHorizontal
        ? `0px ${nearDistance}px`
        : `${nearDistance}px 0px`;

      const warmObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) {
              return;
            }
            this.warmHeroVideo(entry.target as HTMLVideoElement);
            observer.unobserve(entry.target);
          });
        },
        { root: viewport, rootMargin: warmMargin }
      );

      const nearObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) {
              return;
            }
            this.promoteHeroVideo(entry.target as HTMLVideoElement);
            observer.unobserve(entry.target);
          });
        },
        { root: viewport, rootMargin: nearMargin }
      );

      viewportVideos.forEach((video) => {
        warmObserver.observe(video);
        nearObserver.observe(video);
      });
      this.heroVideoObservers.push(warmObserver, nearObserver);
    });

    // IntersectionObserver callbacks for continuously transformed elements can
    // be delayed or skipped in some browsers. Keep a low-frequency geometry
    // check so visible videos are never left behind their placeholders.
    this.updateHeroVideoPreloadByDistance();
    this.heroVideoProximityTimer = setInterval(
      () => this.updateHeroVideoPreloadByDistance(),
      1000
    );
  }

  private updateHeroVideoPreloadByDistance(): void {
    const videos = this.heroVideos
      .toArray()
      .map(({ nativeElement }) => nativeElement)
      .filter((video) => video.isConnected);

    videos.forEach((video) => {
      if (video.dataset['preloadState'] === 'auto') {
        return;
      }

      const viewport = video.closest<HTMLElement>(
        '.video-column, .video-marquee'
      );
      if (!viewport) {
        this.promoteHeroVideo(video);
        return;
      }

      const isHorizontal = viewport.classList.contains('video-marquee');
      const videoRect = video.getBoundingClientRect();
      const viewportRect = viewport.getBoundingClientRect();
      const viewportSize = isHorizontal
        ? viewportRect.width
        : viewportRect.height;

      const distance = isHorizontal
        ? Math.max(
            viewportRect.left - videoRect.right,
            videoRect.left - viewportRect.right,
            0
          )
        : Math.max(
            viewportRect.top - videoRect.bottom,
            videoRect.top - viewportRect.bottom,
            0
          );

      if (distance <= viewportSize * 0.35) {
        this.promoteHeroVideo(video);
      } else if (distance <= viewportSize) {
        this.warmHeroVideo(video);
      }
    });

    if (
      videos.length > 0 &&
      videos.every((video) => video.dataset['preloadState'] === 'auto')
    ) {
      clearInterval(this.heroVideoProximityTimer);
      this.heroVideoProximityTimer = undefined;
    }
  }

  private warmHeroVideo(video: HTMLVideoElement): void {
    if (video.dataset['preloadState']) {
      return;
    }

    this.attachHeroVideoSource(video);
    video.preload = 'metadata';
    video.dataset['preloadState'] = 'metadata';
    video.load();
  }

  private promoteHeroVideo(video: HTMLVideoElement): void {
    if (video.dataset['preloadState'] === 'auto') {
      return;
    }

    this.attachHeroVideoSource(video);
    video.preload = 'auto';
    video.autoplay = true;
    video.defaultMuted = true;
    video.muted = true;
    video.dataset['preloadState'] = 'auto';
    video.load();
    void video.play().catch(() => {
      // The canplay handler retries muted playback when enough data is ready.
    });
  }

  private attachHeroVideoSource(video: HTMLVideoElement): void {
    const source = video.querySelector<HTMLSourceElement>('source[data-src]');
    if (source && !source.src) {
      source.src = source.dataset['src'] ?? '';
    }
  }

  private disconnectHeroVideoObservers(): void {
    this.heroVideoObservers.forEach((observer) => observer.disconnect());
    this.heroVideoObservers = [];
    if (this.heroVideoProximityTimer) {
      clearInterval(this.heroVideoProximityTimer);
      this.heroVideoProximityTimer = undefined;
    }
  }

  onScroll() {
    // Throttle scroll events to reduce CPU usage
    if (!this.scrollThrottleTimer) {
      this.scrollThrottleTimer = setTimeout(() => {
        this.updateThumb();
        this.scrollThrottleTimer = null;
      }, 16); // ~60fps
    }
  }

  updateThumbBound = () => this.updateThumb();

  updateThumb() {
    if (!this.scrollContent || !this.scrollContent.nativeElement) {
      return;
    }
    const el = this.scrollContent.nativeElement;
    const scrollHeight = el.scrollHeight;
    const clientHeight = el.clientHeight;
    const scrollTop = el.scrollTop;
    const thumbHeight = Math.max(
      (clientHeight / scrollHeight) * clientHeight,
      40
    );
    const maxThumbTop = clientHeight - thumbHeight;
    const thumbTop = (scrollTop / (scrollHeight - clientHeight)) * maxThumbTop;
    this.thumbStyle = {
      height: `${thumbHeight}px`,
      top: `${thumbTop}px`,
      display: 'block'
    };
    this.cdr.markForCheck();
  }

  onThumbMouseDown(event: MouseEvent) {
    event.preventDefault();
    this.dragging = true;
    this.startY = event.clientY;
    this.startScrollTop = this.scrollContent.nativeElement.scrollTop;
    if (isPlatformBrowser(this.platformId)) {
      document.addEventListener('mousemove', this.onThumbMouseMoveBound);
      document.addEventListener('mouseup', this.onThumbMouseUpBound);
    }
  }

  onThumbMouseMoveBound = (event: MouseEvent) => this.onThumbMouseMove(event);

  onThumbMouseUpBound = () => this.onThumbMouseUp();

  onThumbMouseMove(event: MouseEvent) {
    if (!this.dragging) {
      return;
    }
    const el = this.scrollContent.nativeElement;
    const scrollHeight = el.scrollHeight;
    const clientHeight = el.clientHeight;
    const thumbHeight = Math.max(
      (clientHeight / scrollHeight) * clientHeight,
      40
    );
    const maxThumbTop = clientHeight - thumbHeight;
    const deltaY = event.clientY - this.startY;
    const scrollableHeight = scrollHeight - clientHeight;
    const newThumbTop = Math.min(
      Math.max(
        (this.startScrollTop / scrollableHeight) * maxThumbTop + deltaY,
        0
      ),
      maxThumbTop
    );
    el.scrollTop = (newThumbTop / maxThumbTop) * scrollableHeight;
  }

  onThumbMouseUp() {
    this.dragging = false;
    if (isPlatformBrowser(this.platformId)) {
      document.removeEventListener('mousemove', this.onThumbMouseMoveBound);
      document.removeEventListener('mouseup', this.onThumbMouseUpBound);
    }
  }

  private applyPageSeoTags(): void {
    const baseUrl = 'https://pitch59.com';
    const routePath = this.router.url.split('?')[0];

    let pageTitle: string;
    let description: string;
    let ogImage: string;
    let canonical: string;

    if (routePath === '/jobs') {
      pageTitle = 'Pitch59 | Get Noticed. Get Hired.';
      description =
        'PitchCards are the most powerful networking tool for job seekers. Share your personalized resume card and get referrals in just 5 seconds.';
      ogImage = `${baseUrl}/assets/images/landing-page/image-resumepage.png`;
      canonical = `${baseUrl}/jobs`;
    } else {
      pageTitle = 'Pitch59 | Stand Out. Be Remembered. Get More Referrals.';
      description =
        "PitchCards are the world's most powerful business card for networking, introductions, and referrals. Create your 59-second elevator pitch and grow your business.";
      ogImage = `${baseUrl}/assets/images/landing-page/image-homepage.webp`;
      canonical = `${baseUrl}/`;
    }

    this.titleService.setTitle(pageTitle);

    if (!this.route.snapshot.url.some((seg) => seg.path.match('card'))) {
      this.upsertMeta({ name: 'description' }, description);
      this.upsertMeta({ property: 'og:type' }, 'website');
      this.upsertMeta({ property: 'og:site_name' }, 'Pitch59');
      this.upsertMeta({ property: 'og:title' }, pageTitle);
      this.upsertMeta({ property: 'og:description' }, description);
      this.upsertMeta({ property: 'og:image' }, ogImage);
      this.upsertMeta({ property: 'og:url' }, canonical);
    }

    let link: HTMLLinkElement = this.doc.querySelector('link[rel="canonical"]');
    if (!link) {
      link = this.doc.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.doc.head.appendChild(link);
    }
    link.setAttribute('href', canonical);
  }

  private upsertMeta(
    selector: { name?: string; property?: string },
    content: string
  ): void {
    const attr = selector.property ? 'property' : 'name';
    const key = selector.property ?? selector.name;
    const existing = this.metaService.getTag(`${attr}='${key}'`);
    if (existing) {
      this.metaService.updateTag({ ...selector, content });
    } else {
      this.metaService.addTag({ ...selector, content });
    }
  }

  private checkQueryParams() {
    const routeAuthAction = this.route.snapshot.data['authAction'];
    if (routeAuthAction === 'SIGN_IN' || routeAuthAction === 'SIGN_UP') {
      this.authService.setAction(
        routeAuthAction,
        this.route.snapshot.queryParams
      );
    }

    this.route.queryParams.subscribe((params) => {
      const isResetPasswordRoute =
        this.route.snapshot.routeConfig?.path === 'reset-password';
      if (isResetPasswordRoute) {
        this.authService.setAction('RESET_PASSWORD', params);
        return;
      }

      switch (params?.type) {
        case 'reset-password':
          this.authService.setAction('RESET_PASSWORD', params);
          break;
        case 'sign-up':
          this.authService.setAction('SIGN_UP', params);
          break;
        case 'sign-in':
          this.authService.setAction('SIGN_IN', params);
          break;
      }
      if (
        !params?.type &&
        params?.token &&
        !this.router.url.includes('tenant-invite')
      ) {
        this.authService.setAction('IMPERSONATE_SIGN_IN', params.token);
      }
      if (params.teamToken && !this.isAuthorized) {
        this.authService.setAction('SIGN_UP', params);
      } else if (params.teamToken && this.isAuthorized) {
        this.authService.setAction('TEAM_TOKEN', params);
      }
    });
  }

  private initSliderSubscriptions() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const swipeZone = document.getElementById('slider-video');
    if (swipeZone) {
      swipeZone.addEventListener('touchstart', (event: TouchEvent) => {
        this.startX = event.touches[0].clientX;
        this.startY = event.touches[0].clientY;
      });

      swipeZone.addEventListener('touchmove', (event: TouchEvent) => {
        this.endX = event.touches[0].clientX;
        this.endY = event.touches[0].clientY;
      });

      swipeZone.addEventListener('touchend', () => {
        const diffX = this.endX - this.startX;
        const diffY = this.endY - this.startY;

        if (
          Math.abs(diffX) > this.threshold &&
          Math.abs(diffX) > Math.abs(diffY)
        ) {
          if (diffX > 20) {
            this.moveToSlide(this.activeVideo - 1);
          } else {
            this.moveToSlide(this.activeVideo + 1);
          }
        }
        this.startX = 0;
        this.startY = 0;
        this.endX = 0;
        this.endY = 0;
      });
    }
  }

  private stopVideoPlaying() {
    this.slides.forEach((x) => {
      if (x.businessDetails) {
        x.stopVideo();
      }
    });
  }

  private getPreviewCardsList() {
    this.userCommonService
      .getPitchListPreview()
      .pipe(
        map(({ data }) => {
          const forwardRecords = data?.forwardRecords.sort((a, b) => {
            // Sort by 'alias' with 'pitch' coming first
            if (a.alias === 'pitch59-inc') {
              return -1;
            }
            if (b.alias === 'pitch59-inc') {
              return 1;
            }
            return 0;
          });
          
          forwardRecords?.map((el, index) => {
            el.index = index;
          });
          return { ...data, forwardRecords };
        })
      )
      .subscribe((data) => {
        if (data.forwardRecords?.length) {
          // TODO: Remove this when the API is updated
          data.forwardRecords.forEach((el, index) => (el.isShareable = true));

          this.previewCardsList = [];
          this.previewCardsList = Array.from({
            length: data.totalRecords || 0
          }).map(
            (x, i) => <BusinessPitchWrapped>{ index: i, business: undefined }
          );
          data.forwardRecords.forEach((el) => {
            if (el && !el.videoCoverImageThumbnailUrl) {
              el.videoCoverImageThumbnailUrl = 'assets/images/comingsoon.png';
            }

            if (el && !el.businessLogoThumbnailUrl) {
              el.businessLogoThumbnailUrl = '';
            }
            this.previewCardsList[el.index].business = el;
          });
          this.isListLoaded = true;
          setTimeout(() => {
            if (!this.activeVideo) {
              this.activeVideo = 0;
            }
          }, 0);
        } else {
          this.previewCardsList = [];
        }
      });
  }

  private startTimer(): void {
    // Only start timer if page is visible and we're in browser
    if (
      isPlatformBrowser(this.platformId) &&
      document.visibilityState === 'visible'
    ) {
      this.slideTimer = setInterval(() => {
        this.nextSlide();
      }, 5000);
    }
  }

  private resetTimer(): void {
    if (this.slideTimer) {
      clearInterval(this.slideTimer);
    }
    this.startTimer();
  }

  private nextSlide(): void {
    this.activeVideo = (this.activeVideo + 1) % this.sliderList.length;
  }

  private handleVisibilityChange = () => {
    if (
      isPlatformBrowser(this.platformId) &&
      document.visibilityState === 'visible'
    ) {
      this.startTimer();
    } else {
      this.resetTimer();
    }
  };
}
